import type { FC, ReactNode } from 'react'
import type { DoorStyle } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { DoorCard } from '@/components/widgets/door-card'
import { doors, doorStyles } from '@/constants/doors'

type RailFilter = DoorStyle | 'All'

export const DoorRail: FC = (): ReactNode => {
    const [filter, setFilter] = useState<RailFilter>('All')
    const [progress, setProgress] = useState(0)
    const scrollerRef = useRef<HTMLDivElement>(null)

    const list = filter === 'All' ? doors : doors.filter(door => door.style === filter)

    const measure = () => {
        const scroller = scrollerRef.current

        if (!scroller) return

        const max = scroller.scrollWidth - scroller.clientWidth

        setProgress(max > 0 ? scroller.scrollLeft / max : 1)
    }

    useEffect(() => {
        const scroller = scrollerRef.current

        if (!scroller) return

        scroller.scrollTo({ left: 0 })
        measure()
    }, [filter])

    const scrollByCard = (direction: 1 | -1) => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>('[data-rail-card]')

        if (!scroller || !card) return

        scroller.scrollBy({ left: direction * (card.offsetWidth + 20), behavior: 'smooth' })
    }

    const filters: RailFilter[] = ['All', ...doorStyles]

    return (
        <div>
            <Container className='flex flex-wrap items-center justify-between gap-4'>
                <div className='no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1'>
                    {filters.map(option => (
                        <button
                            key={option}
                            type='button'
                            aria-pressed={filter === option}
                            onClick={() => setFilter(option)}
                            className={cn(
                                'h-10 shrink-0 rounded-full border px-4 text-[14px] font-medium transition-colors duration-300',
                                filter === option ? 'border-ink bg-ink text-cream' : 'border-ink/12 text-ink hover:border-ink/35'
                            )}
                        >
                            {option}
                        </button>
                    ))}
                </div>

                <div className='hidden items-center gap-2 md:flex'>
                    <button
                        type='button'
                        onClick={() => scrollByCard(-1)}
                        aria-label="Scroll to previous doors"
                        className='flex size-12 items-center justify-center rounded-full border border-ink/15 transition-colors hover:bg-ink hover:text-cream'
                    >
                        <ArrowLeft className='size-4' />
                    </button>

                    <button
                        type='button'
                        onClick={() => scrollByCard(1)}
                        aria-label="Scroll to next doors"
                        className='flex size-12 items-center justify-center rounded-full border border-ink/15 transition-colors hover:bg-ink hover:text-cream'
                    >
                        <ArrowRight className='size-4' />
                    </button>
                </div>
            </Container>

            <div
                ref={scrollerRef}
                onScroll={measure}
                className='no-scrollbar mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-2 sm:scroll-px-6 sm:px-6 md:scroll-px-8 md:px-8 xl:scroll-px-[max(2.5rem,calc((100vw_-_1360px)/2_+_2.5rem))] xl:px-[max(2.5rem,calc((100vw_-_1360px)/2_+_2.5rem))]'
            >
                {list.map(door => (
                    <div key={door.id} data-rail-card className='w-[84vw] shrink-0 snap-start sm:w-[23rem] lg:w-[25rem]'>
                        <DoorCard door={door} className='h-full' sizes='(min-width: 1024px) 400px, 84vw' />
                    </div>
                ))}
            </div>

            <Container className='mt-8'>
                <div className='h-px w-full bg-ink/10' aria-hidden='true'>
                    <div
                        className='h-px bg-ink transition-[width] duration-300'
                        style={{ width: `${Math.max(12, progress * 100)}%` }}
                    />
                </div>
            </Container>
        </div>
    )
}
